import { useTranslation } from 'react-i18next'
import { Link, Outlet, useLocation } from 'react-router-dom'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Separator } from '@/components/ui/separator'
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar'
import { TooltipProvider } from '@/components/ui/tooltip'
import { AppSidebar } from '@/components/layout/AppSidebar'
import { APP_NAME } from '@/components/layout/brand'
import { activeItem } from '@/components/layout/navigation'

export function AppLayout() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const current = activeItem(pathname)

  return (
    // One provider for the whole signed-in app: the sidebar puts a tooltip on every collapsed
    // item, and the pages below add their own.
    <TooltipProvider>
      {/* The shell is exactly as tall as the viewport, and the scrolling happens inside it.
          `min-h-svh` — what the provider brings — lets the page grow instead, and a screen that
          wants to pin something to its own bottom edge then has no bottom edge to pin it to. */}
      <SidebarProvider className="h-svh overflow-hidden">
        <AppSidebar />
        <SidebarInset>
          <header className="bg-background/95 supports-[backdrop-filter]:bg-background/80 sticky top-0 z-10 flex h-14 shrink-0 items-center gap-2 border-b px-4 backdrop-blur">
            <SidebarTrigger className="-ml-1" aria-label={t('nav.toggleSidebar')} />
            <Separator
              orientation="vertical"
              className="mr-1 data-vertical:h-4 data-vertical:self-auto"
            />
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem className="hidden sm:block">
                  <BreadcrumbLink asChild>
                    <Link to="/">{APP_NAME}</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="hidden sm:block" />
                <BreadcrumbItem>
                  {/* A route outside the navigation cannot reach this layout today, but naming
                      the product beats rendering an empty crumb if one ever does. */}
                  <BreadcrumbPage>{current ? t(current.labelKey) : APP_NAME}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </header>

          {/* Not a <main>: SidebarInset already is one.

              This is where an ordinary screen scrolls — the dashboard and the catalogue are as
              long as their content and simply run past the bottom. `min-h-0` is what lets a screen
              hand its height back instead: a flex child cannot otherwise be shorter than its
              content, and a list that fills the space and scrolls inside itself would push the
              page down rather than keeping its own footer in view. */}
          <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-4 md:p-6">
            <Outlet />
          </div>
        </SidebarInset>
      </SidebarProvider>
    </TooltipProvider>
  )
}
